import { useCallback } from 'react';

import { useAuth } from '@shared/common/providers/AuthProvider';
import {
  useAuthModal,
  useCompleteMagicIdModal,
} from '@shared/common/providers/GlobalModalsProvider';

import { useSelfHumanityScoreQuery } from '@query/user';

import useUserWalletStatusFlags from '../helpers/web3/hooks/useUserWalletStatusFlags';

const useCheckRequiredSelfData = () => {
  const { isAuth, user } = useAuth();
  const { open: openAuthModal } = useAuthModal();
  const { open: openCompleteMagicIdModal } = useCompleteMagicIdModal();
  const { isEMVWalletConnected: hasEvmWallet } = useUserWalletStatusFlags();
  const { data: gitcoin, isPending: isHumanityVerificationLoading } = useSelfHumanityScoreQuery({
    enabled: isAuth,
  });
  const hasUsername = !!user?.attributes.name;
  const hasTonWallet = !!user?.wallets.find((wallet) => wallet.attributes.network === 'TON');
  const hasSolanaWallet = !!user?.wallets.find((wallet) => wallet.attributes.network === 'SOLANA');
  const hasEmail = !!user?.attributes.email;
  const hasLinkedTelegram = !!user?.socials.find((social) => social.type === 'TELEGRAM');
  const hasFaceVerification = !!user?.attributes.faceCheckPassed;
  const hasGitcoinVerification = !!gitcoin?.gitCoin?.acceptable;

  const check = useCallback(
    ({
      username,
      auth,
      email,
      evmWallet,
      acceptableGitcoin,
    }: {
      username?: boolean;
      auth?: boolean;
      email?: boolean;
      evmWallet?: boolean;
      acceptableGitcoin?: boolean;
    }) => {
      if (auth && !isAuth) {
        openAuthModal();

        return false;
      }

      const missingData: string[] = [];

      if (username && !hasUsername) {
        missingData.push('email');
      }

      if (email && !hasEmail) {
        missingData.push('email');
      }

      if (evmWallet && !hasEvmWallet) {
        missingData.push('evm');
      }

      if (acceptableGitcoin && !hasGitcoinVerification && !hasFaceVerification) {
        missingData.push('humanityScore');
      }

      if (missingData.length) {
        openCompleteMagicIdModal({
          description: {
            ...(missingData.includes('name') && { name: null }),
            ...(missingData.includes('email') && { email: null }),
            ...(missingData.includes('evm') && { wallets: { EVM: null } }),
            ...(missingData.includes('humanityScore') && { humanityScore: { acceptable: false } }),
          },
          success: false,
        });

        return false;
      }

      return true;
    },
    [isAuth, hasEmail, hasEvmWallet, openAuthModal, openCompleteMagicIdModal],
  );

  return {
    isAuth,
    hasEmail,
    hasEvmWallet,
    hasTonWallet,
    hasSolanaWallet,
    hasUsername,
    hasFaceVerification,
    hasGitcoinVerification,
    isHumanityVerificationLoading,
    hasLinkedTelegram,
    check,
  };
};

export default useCheckRequiredSelfData;
